import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import KPICard from '../../components/reports/KPICard';
import ReportChart from '../../components/reports/ReportChart';
import ReportTable from '../../components/reports/ReportTable';
import BranchSelect from '../../components/reports/BranchSelect';
import {
  calculateWarningKPIs,
  getRiskDistributionData,
  getTrendData,
  getWarningsByCustomerType,
  getModelPerformance,
  filterByDateRange,
  filterByStatus,
  filterByRiskLevel,
  getUniqueBranches,
  filterByBranch,
  exportToCSV
} from '../../utils/reportHelpers';

function SummaryReport() {
  const navigate = useNavigate();
  const { warnings, customers, models } = useData();
  
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    riskLevel: 'all',
    branch: []
  });

  const branches = getUniqueBranches(customers);

  // Apply filters
  let filteredWarnings = warnings;
  filteredWarnings = filterByDateRange(filteredWarnings, filters.dateFrom, filters.dateTo);
  filteredWarnings = filterByStatus(filteredWarnings, filters.status);
  filteredWarnings = filterByRiskLevel(filteredWarnings, filters.riskLevel);
  filteredWarnings = filterByBranch(filteredWarnings, customers, filters.branch);

  const kpis = calculateWarningKPIs(filteredWarnings);
  const riskDistData = getRiskDistributionData(filteredWarnings);
  const trendData = getTrendData(filteredWarnings, 30);
  const customerTypeData = getWarningsByCustomerType(filteredWarnings);
  const modelPerfData = getModelPerformance(filteredWarnings, models);

  // New charts data from image
  // 1. Grouped bar chart: Risk levels by customer type
  const getRiskByCustomerTypeData = () => {
    const khcn = filteredWarnings.filter(w => w.customerType === 'Khách hàng cá nhân');
    const khdn = filteredWarnings.filter(w => w.customerType === 'Khách hàng doanh nghiệp');
    
    return {
      labels: ['KHCN', 'KHDN'],
      datasets: [
        {
          label: 'Cảnh báo xanh',
          data: [
            khcn.filter(w => w.riskLevel === 'low').length,
            khdn.filter(w => w.riskLevel === 'low').length
          ],
          backgroundColor: '#28a745'
        },
        {
          label: 'Cảnh báo vàng',
          data: [
            khcn.filter(w => w.riskLevel === 'medium').length,
            khdn.filter(w => w.riskLevel === 'medium').length
          ],
          backgroundColor: '#ffc107'
        },
        {
          label: 'Cảnh báo đỏ',
          data: [
            khcn.filter(w => w.riskLevel === 'high').length,
            khdn.filter(w => w.riskLevel === 'high').length
          ],
          backgroundColor: '#dc3545'
        }
      ]
    };
  };

  // 2. Pie chart: Distribution by status/type
  const getWarningDistributionData = () => {
    const statusCounts = {};
    filteredWarnings.forEach(w => {
      const key = w.status || 'unknown';
      statusCounts[key] = (statusCounts[key] || 0) + 1;
    });

    return {
      labels: Object.keys(statusCounts).map(s => {
        if (s === 'new') return 'Mới phát sinh';
        if (s === 'in_progress') return 'Đang xử lý';
        if (s === 'resolved') return 'Đã xử lý';
        return s;
      }),
      datasets: [{
        data: Object.values(statusCounts),
        backgroundColor: ['#0d6efd', '#17a2b8', '#28a745', '#ffc107', '#dc3545'],
        borderWidth: 0
      }]
    };
  };

  // 3. Stacked horizontal bar: Percentage distribution by customer type
  const getStackedPercentageData = () => {
    const khcn = filteredWarnings.filter(w => w.customerType === 'Khách hàng cá nhân');
    const khdn = filteredWarnings.filter(w => w.customerType === 'Khách hàng doanh nghiệp');

    const calculatePercent = (arr) => {
      const total = arr.length || 1;
      return {
        low: (arr.filter(w => w.riskLevel === 'low').length / total) * 100,
        medium: (arr.filter(w => w.riskLevel === 'medium').length / total) * 100,
        high: (arr.filter(w => w.riskLevel === 'high').length / total) * 100
      };
    };

    const khcnPercent = calculatePercent(khcn);
    const khdnPercent = calculatePercent(khdn);

    return {
      labels: ['KHCN', 'KHDN'],
      datasets: [
        {
          label: 'Cảnh báo xanh',
          data: [khcnPercent.low, khdnPercent.low],
          backgroundColor: '#28a745'
        },
        {
          label: 'Cảnh báo vàng',
          data: [khcnPercent.medium, khdnPercent.medium],
          backgroundColor: '#ffc107'
        },
        {
          label: 'Cảnh báo đỏ',
          data: [khcnPercent.high, khdnPercent.high],
          backgroundColor: '#dc3545'
        }
      ]
    };
  };

  const riskByTypeData = getRiskByCustomerTypeData();
  const distributionData = getWarningDistributionData();
  const stackedPercentData = getStackedPercentageData();

  const tableColumns = [
    { key: 'code', label: 'Mã CB', sortable: true },
    { key: 'customerName', label: 'Khách hàng', sortable: true },
    { key: 'customerType', label: 'Loại KH' },
    { key: 'modelName', label: 'Mô hình' },
    { key: 'score', label: 'Điểm', sortable: true, type: 'number' },
    { key: 'riskLevel', label: 'Mức rủi ro', sortable: true },
    { key: 'status', label: 'Trạng thái', sortable: true },
    { key: 'createdDate', label: 'Ngày tạo', sortable: true, type: 'date' },
    { key: 'assignedTo', label: 'Người xử lý' }
  ];

  const handleExport = () => {
    const exportData = filteredWarnings.map(w => ({
      'Mã': w.code,
      'Khách hàng': w.customerName,
      'Loại KH': w.customerType,
      'Mô hình': w.modelName,
      'Điểm': w.score,
      'Mức rủi ro': w.riskLevel,
      'Trạng thái': w.status,
      'Ngày tạo': w.createdDate,
      'Người xử lý': w.assignedTo || 'Chưa phân công'
    }));
    exportToCSV(exportData, 'bao-cao-tong-hop');
  };

  const handleRowClick = (warning) => {
    navigate('/warnings', { state: { selectedWarning: warning } });
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="page-header-sticky">
        <div className="row">
          <div className="col-12">
            <button className="btn btn-outline-secondary mb-2" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
            <h2 className="mb-1">
              <i className="bi bi-file-earmark-text me-2 text-primary"></i>
              Báo cáo tổng hợp các trường hợp cảnh báo sớm
            </h2>
            <p className="text-muted mb-0">
              Tổng quan toàn bộ hệ thống cảnh báo với các chỉ số và xu hướng
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-funnel me-2"></i>
                Bộ lọc
              </h5>
              <div className="row g-3">
                <div className="col-md-2">
                  <BranchSelect 
                    value={filters.branch}
                    onChange={(value) => setFilters({...filters, branch: value})}
                    branches={branches}
                    label="Chi nhánh"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Từ ngày</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Đến ngày</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Trạng thái</label>
                  <select 
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="all">Tất cả</option>
                    <option value="new">Mới</option>
                    <option value="in_progress">Đang xử lý</option>
                    <option value="resolved">Đã xử lý</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Mức rủi ro</label>
                  <select 
                    className="form-select"
                    value={filters.riskLevel}
                    onChange={(e) => setFilters({...filters, riskLevel: e.target.value})}
                  >
                    <option value="all">Tất cả</option>
                    <option value="high">Cao</option>
                    <option value="medium">Trung bình</option>
                    <option value="low">Thấp</option>
                  </select>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={() => setFilters({ dateFrom: '', dateTo: '', status: 'all', riskLevel: 'all', branch: [] })}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <KPICard 
            title="Tổng số cảnh báo"
            value={kpis.total}
            icon="bi-clipboard-data"
            color="primary"
            subtitle={`${kpis.newIn7Days} mới trong 7 ngày`}
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Rủi ro cao"
            value={kpis.highRisk}
            icon="bi-exclamation-triangle-fill"
            color="danger"
            subtitle={`${kpis.highRiskPercent}% tổng số`}
            onClick={() => navigate('/reports/high-risk')}
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Điểm trung bình"
            value={kpis.avgScore}
            icon="bi-speedometer2"
            color="info"
            subtitle="Trên thang 100 điểm"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Thời gian xử lý TB"
            value={`${kpis.avgResolutionTime} ngày`}
            icon="bi-clock-history"
            color="success"
            subtitle={`${kpis.resolved} đã xử lý`}
          />
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-4">
          <ReportChart 
            type="doughnut"
            title="Phân bố theo mức rủi ro"
            data={riskDistData}
            height={300}
          />
        </div>
        <div className="col-md-8">
          <ReportChart 
            type="line"
            title="Xu hướng 30 ngày"
            data={trendData}
            height={300}
            options={{
              scales: {
                x: { 
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: false,
                    padding: 20,
                    font: { size: 9 }
                  }
                },
                y: { beginAtZero: true }
              }
            }}
          />
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Phân bố theo loại khách hàng"
            data={customerTypeData}
            height={300}
          />
        </div>
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Hiệu suất mô hình"
            data={modelPerfData}
            height={300}
            options={{
              plugins: {
                legend: { position: 'bottom' }
              },
              scales: {
                x: { 
                  stacked: true,
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: false,
                    padding: 20,
                    font: { size: 9 }
                  }
                },
                y: { stacked: true, beginAtZero: true }
              }
            }}
          />
        </div>
      </div>

      {/* Charts Row 3 - New from image */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Báo cáo 18 đã phân tích dữ liệu cảnh báo sớm theo các loại cơ cảnh báo"
            data={riskByTypeData}
            height={300}
            options={{
              plugins: {
                legend: { position: 'top' }
              }
            }}
          />
        </div>
        <div className="col-md-6">
          <ReportChart 
            type="pie"
            title="Tỷ lệ cảnh báo"
            data={distributionData}
            height={300}
            options={{
              plugins: {
                legend: { position: 'right' }
              }
            }}
          />
        </div>
      </div>

      {/* Charts Row 4 - Stacked percentage */}
      <div className="row g-3 mb-4 animate-fade-in-up">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Báo cáo 18 phân trầm sở lượng các loại cơ cảnh báo theo các phần khác</h5>
              <ReportChart 
                type="bar"
                data={stackedPercentData}
                height={200}
                options={{
                  indexAxis: 'y',
                  plugins: {
                    legend: { position: 'bottom' }
                  },
                  scales: {
                    x: { 
                      stacked: true,
                      max: 100,
                      ticks: {
                        callback: (value) => value + '%'
                      }
                    },
                    y: { stacked: true }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Danh sách chi tiết ({filteredWarnings.length} bản ghi)</h4>
            <button className="btn btn-success" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Xuất Excel
            </button>
          </div>
          <ReportTable 
            data={filteredWarnings}
            columns={tableColumns}
            onRowClick={handleRowClick}
            pageSize={10}
          />
        </div>
      </div>
    </div>
  );
}

export default SummaryReport;
